import React, { createContext, useContext, useEffect, useState } from "react";

export interface Season {
  id: string | number;
  name: string;
}

interface SeasonContextType {
  seasons: Season[];
  selectedSeason: string;
  seasonName: string;
  setSelectedSeason: (id: string) => void;
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

export const useSeason = () => {
  const ctx = useContext(SeasonContext);
  if (!ctx) throw new Error("useSeason must be used within a SeasonProvider");
  return ctx;
};

export const SeasonProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeasonState] = useState<string>("");
  const [seasonName, setSeasonName] = useState<string>("");

  // Load selected season from localStorage on mount
  useEffect(() => {
    const fetchSeasons = async () => {
      const res = await fetch(
        (
          await import("../apiConfig")
        ).API_ENDPOINTS.seasons
      );
      const data = await res.json();
      setSeasons(data);
      // Try to load from localStorage
      const stored = localStorage.getItem("selectedSeason");
      let selectedId = "";
      if (stored && data.some((s: Season) => s.id.toString() === stored)) {
        selectedId = stored;
        setSelectedSeasonState(stored);
      } else if (data.length > 0) {
        selectedId = data[0].id.toString();
        setSelectedSeasonState(selectedId);
      }
      // Set seasonName from DB
      const found = data.find((s: Season) => s.id.toString() === selectedId);
      setSeasonName(found ? found.name : "");
    };
    fetchSeasons();
  }, []);

  // Save selected season to localStorage whenever it changes
  useEffect(() => {
    if (selectedSeason) {
      localStorage.setItem("selectedSeason", selectedSeason);
      // Update seasonName from DB
      const found = seasons.find(
        (s: Season) => s.id.toString() === selectedSeason
      );
      setSeasonName(found ? found.name : "");
    }
  }, [selectedSeason, seasons]);

  // Wrap setter to persist to localStorage
  const setSelectedSeason = (id: string) => {
    setSelectedSeasonState(id);
    localStorage.setItem("selectedSeason", id);
    const found = seasons.find((s: Season) => s.id.toString() === id);
    setSeasonName(found ? found.name : "");
  };

  return (
    <SeasonContext.Provider
      value={{ seasons, selectedSeason, seasonName, setSelectedSeason }}
    >
      {children}
    </SeasonContext.Provider>
  );
};
